<script setup>
const props = defineProps({
  title: {
    type: String,
    default: undefined,
  },
  description: {
    type: String,
    default: undefined,
  },
  iconName: {
    type: String,
    default: undefined,
  },

  max: {
    type: Number,
    default: 100,
  },
  min: {
    type: Number,
    default: 0,
  },
  step: {
    type: Number,
    default: 1,
  },

  withQuestion: {
    type: Boolean,
    default: false,
  },

  totalCount: {
    type: Number,
    default: 0,
  },

  modelValue: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['update:modelValue', 'questionClick']);

const isActive = computed(() => props.modelValue > 0);
const isMax = computed(
  () =>
    props.modelValue + props.step > props.max || props.totalCount >= props.max,
);
const isMin = computed(() => props.modelValue - props.step < props.min);

function increment() {
  if (isMax.value) return;

  emit('update:modelValue', props.modelValue + props.step);
}

function decrement() {
  if (isMin.value) return;

  emit('update:modelValue', props.modelValue - props.step);
}
</script>

<template>
  <div class="ui-counter" :class="{ 'ui-counter--active': isActive }">
    <div class="ui-counter__head">
      <CIcon
        v-if="iconName"
        class="ui-counter__icon ui-counter__icon--head"
        :name="iconName"
      />

      <div class="ui-counter__title">
        <span class="ui-counter__font ui-counter__font--title">
          {{ title }}
        </span>
      </div>

      <div class="ui-counter__description">
        <span class="ui-counter__font ui-counter__font--description">
          {{ description }}
        </span>
      </div>

      <CIcon
        v-if="withQuestion"
        class="ui-counter__icon ui-counter__icon--question"
        name="question-mark"
        @click="emit('questionClick')"
      />
    </div>

    <div class="ui-counter__wrapper">
      <button
        type="button"
        class="ui-counter__button"
        :disabled="isMin"
        @click="decrement"
      >
        <CIcon class="ui-counter__icon ui-counter__icon--action" name="minus" />
      </button>

      <div class="ui-counter__value">
        <span class="ui-counter__font--value">
          {{ modelValue }}
        </span>
      </div>

      <button
        type="button"
        class="ui-counter__button"
        :disabled="isMax"
        @click="increment"
      >
        <CIcon class="ui-counter__icon ui-counter__icon--action" name="plus" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-counter {
  $parent: &;

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 em(19) 0 em(13);
  background-color: $color-gray-5;
  border: 1px solid $color-gray-5;
  border-radius: em(5);
  transition: $time-3;
  transition-property: background-color, border-color;

  &__font {
    display: block;

    &--title {
      font-size: em(14);
      font-weight: 500;
      line-height: math.div(16, 14);
    }

    &--description {
      font-size: em(14);
      font-weight: 400;
      line-height: math.div(18, 14);
    }

    &--value {
      font-size: em(36);
      font-weight: 500;
      line-height: math.div(56, 36);
    }
  }

  &__icon {
    &--head {
      @include box(em(32));

      flex-shrink: 0;
      margin-right: em(5);
      color: $color-gray-4;
      transition: color $time-3;
    }

    &--action {
      @include box(em(28));
    }

    &--question {
      @include box(em(17));

      flex-shrink: 0;
      margin-left: em(4);
      color: $color-main-1;
      cursor: pointer;
      transition: color $time-3;

      @include hover {
        color: $color-second-1;
      }
    }
  }

  &__head {
    display: flex;
    align-items: center;
  }

  &__title {
    flex-shrink: 0;
    margin-right: em(4);
  }

  &__description {
    color: $color-gray-4;
  }

  &__wrapper {
    display: flex;
    gap: em(20);
    align-items: center;
    justify-content: space-between;
  }

  &__button {
    color: $color-main-1;
    transition: color $time-3;

    &:disabled {
      color: $color-gray-3;
    }
  }

  &__value {
    min-width: em(44);
    color: $color-gray-3;
    text-align: center;
    transition: color $time-3;
  }

  &--active {
    background-color: $color-main-7;
    border-color: $color-main-1;

    #{$parent} {
      &__icon {
        &--head {
          color: $color-main-1;
        }
      }

      &__value {
        color: $color-main-1;
      }
    }
  }
}
</style>
